import { useEffect, useRef } from 'react';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import Lenis from 'lenis';
import { ArrowDown, ShieldCheck, UserPlus, Zap } from 'lucide-react';
import 'lenis/dist/lenis.css';

const EASE = [0.22, 1, 0.36, 1];

export const useLandingScroll = (enabled) => {
  const reduced = useReducedMotion();
  useEffect(() => {
    if (!enabled || reduced) return undefined;
    const scroll = new Lenis({
      autoRaf: true,
      duration: 1.15,
      smoothWheel: true,
      syncTouch: false,
      allowNestedScroll: true,
      prevent: (node) => Boolean(node.closest?.('textarea, input, [contenteditable]:not([contenteditable="false"]), [data-lenis-prevent]')),
      anchors: { offset: -96, duration: 1.25 },
    });
    return () => scroll.destroy();
  }, [enabled, reduced]);
};

export const Reveal = ({ children, className, delay = 0 }) => {
  const reduced = useReducedMotion();
  return (
    <motion.div className={className} initial={reduced ? false : { opacity: 0, y: 35 }}
      whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
      animate={reduced ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: reduced ? 0 : 0.6, delay: reduced ? 0 : delay, ease: EASE }}>
      {children}
    </motion.div>
  );
};

export const ReadingProgress = () => {
  const { scrollYProgress } = useScroll();
  const reduced = useReducedMotion();
  if (reduced) return null;
  return <motion.div className="sentinel-reading-progress" style={{ scaleX: scrollYProgress }} aria-hidden="true" />;
};

export const CinematicHero = ({ t, loginButton, assets }) => {
  const stage = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: stage, offset: ['start start', 'end start'] });
  const progress = useSpring(scrollYProgress, { stiffness: 110, damping: 30, restDelta: 0.001 });
  const scale = useTransform(progress, [0, 0.6, 1], [1.02, 1.3, 1.45]);
  const imageY = useTransform(progress, [0, 1], ['0%', '12%']);
  const titleY = useTransform(progress, [0, 0.65], [0, -150]);
  const titleScale = useTransform(progress, [0, 0.65], [1, 0.87]);
  const titleOpacity = useTransform(progress, [0, 0.28, 0.62], [1, 1, 0]);
  const horizonOpacity = useTransform(progress, [0, 0.2, 0.8], [0.15, 0.3, 0.95]);

  return (
    <section className={'sentinel-hero-shell' + (reduced ? ' sentinel-static' : '')} ref={stage} aria-labelledby="sentinel-title">
      <div className="sentinel-hero-sticky">
        <div className="sentinel-hero">
          <motion.img className="sentinel-hero-art" src={assets + '/sentinel-orbit.png'} alt="" width="1672" height="941" fetchPriority="high"
            style={reduced ? undefined : { scale, y: imageY }} />
          <motion.div className="sentinel-hero-horizon" style={{ opacity: reduced ? 0.3 : horizonOpacity }} aria-hidden="true" />
          <div className="sentinel-hero-coordinate" aria-hidden="true"><span>TNS / SENTINEL</span><span>COMMUNITY CONTROL SYSTEM</span></div>
          <motion.div className="sentinel-wrap sentinel-hero-content" style={reduced ? undefined : { y: titleY, scale: titleScale, opacity: titleOpacity }}>
            <motion.p className="sentinel-eyebrow" initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduced ? 0 : 1, delay: reduced ? 0 : 0.15, ease: EASE }}>{t('للمجتمعات التي تطمح لأكثر', 'FOR COMMUNITIES BUILT FOR MORE')}</motion.p>
            <h1 id="sentinel-title"><motion.span initial={reduced ? false : { opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduced ? 0 : 1.1, delay: reduced ? 0 : 0.2, ease: EASE }}>TNS</motion.span><span className="sentinel-title-mask"><motion.strong initial={reduced ? false : { y: '105%' }} animate={{ y: '0%' }} transition={{ duration: reduced ? 0 : 1.35, delay: reduced ? 0 : 0.25, ease: EASE }}>SENTINEL</motion.strong></span></h1>
            <motion.div className="sentinel-hero-bottom" initial={reduced ? false : { opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduced ? 0 : 1, delay: reduced ? 0 : 0.65, ease: EASE }}>
              <div className="sentinel-hero-copy"><h2>{t('مجتمعك. تحت قيادتك.', 'Your community. Your command.')}</h2><p>{t('الحماية، المستويات، والترحيب. أنظمة مترابطة تمنح سيرفرك هويته، وتمنحك التحكم.', 'Moderation, leveling, and welcome. Connected systems that give your Discord server its identity, and put you in control.')}</p></div>
              <div className="sentinel-hero-actions">{loginButton('', 'discord-login-btn')}<a className="sentinel-button sentinel-button-ghost" href="#systems">{t('اكتشف الأنظمة', 'Explore systems')}<ArrowDown size={17} /></a></div>
            </motion.div>
          </motion.div>
        </div>
        <a href="#systems" className="sentinel-next-chapter">
          <span><span className="sentinel-chapter-number">01</span>{t('اكتشف أنظمة Sentinel', 'EXPLORE THE SYSTEMS')}</span>
          <span className="sentinel-chapter-icons" aria-hidden="true"><ShieldCheck size={15} /><Zap size={15} /><UserPlus size={15} /></span>
          <ArrowDown className="sentinel-scroll-arrow" size={17} />
        </a>
      </div>
    </section>
  );
};

const ManifestoWord = ({ word, progress, start, end }) => {
  const color = useTransform(progress, [start, end], ['#9ca9ba', '#eff7ff']);
  const reduced = useReducedMotion();
  return <motion.span style={{ color: reduced ? '#eff7ff' : color }}>{word}{' '}</motion.span>;
};

export const SentinelManifesto = ({ t }) => {
  const section = useRef(null);
  const { scrollYProgress } = useScroll({ target: section, offset: ['start 85%', 'end 65%'] });
  const words = t('حضور أقوى. مجتمع أكثر تفاعلًا. وكل التفاصيل تحت قيادتك.', 'More presence. More connection. Every detail under your command.').split(' ');
  return (
    <section className="sentinel-manifesto sentinel-wrap" ref={section} aria-label={t('رؤية Sentinel', 'The Sentinel vision')}>
      <span className="sentinel-kicker">{t('مصمّم حول مجتمعك', 'BUILT AROUND YOUR COMMUNITY')}</span>
      <p>{words.map((word, index) => <ManifestoWord key={index} word={word} progress={scrollYProgress} start={index / words.length} end={(index + 1) / words.length} />)}</p>
      <div className="sentinel-manifesto-signature" aria-hidden="true"><span>TNS</span><span>SENTINEL</span><span>CONTROL / CONNECT / GROW</span></div>
    </section>
  );
};
